import { useState, useMemo, useEffect, useCallback, useRef, memo } from "react";
import { Button, Collapse, Grid, styled } from "@mui/material";
import DownIcon from "@icons-rf/arrows/cheveron-down.svg?react";
import UpIcon from "@icons-rf/arrows/cheveron-up.svg?react";
import MxTypography from "./MxTypography";
import { debounce } from "lodash";
import { EFontFamilies } from "@/shared/theme/theme";

interface MXDescriptionToggleProps {
  text: string;
  initialCollapsed?: boolean;
  color?: string;
  size?: number;
  collapsedSize?: number;
  lines?: number;
  weight?: number;
}

const MXDescriptionToggle = (props: MXDescriptionToggleProps) => {
  const {
    text,
    initialCollapsed = false,
    collapsedSize = 80,
    color = "grey.600",
    lines = 4,
    weight = 400
  } = props;

  const contentRef = useRef<HTMLElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(!initialCollapsed);
  const [isClosing, setIsClosing] = useState(false);
  const [canToggle, setCanToggle] = useState(false);

  const checkTruncate = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;

    const fullHeight = el.scrollHeight;
    const computedStyle = window.getComputedStyle(el);
    const parsedLineHeight = parseFloat(computedStyle.lineHeight);
    const linesHeight =
      !Number.isNaN(parsedLineHeight) && lines
        ? parsedLineHeight * lines
        : 0;
    const threshold = Math.max(collapsedSize ?? 0, linesHeight);
    const isOverflowing =
      threshold > 0
        ? fullHeight - threshold > 1
        : fullHeight > collapsedSize;

    setCanToggle(isOverflowing);
    if (!isOverflowing) {
      setIsExpanded(prev => {
        if (!prev) {
          setIsClosing(false);
          return true;
        }
        return prev;
      });
    }
  }, [collapsedSize, lines]);

  const checkTruncateDebounced = useMemo(
    () =>
      debounce(() => {
        checkTruncate();
      }, 200),
    [checkTruncate]
  );

  useEffect(() => {
    checkTruncate();
  }, [text, checkTruncate]);

  useEffect(() => {
    window.addEventListener("resize", checkTruncateDebounced);
    return () => {
      window.removeEventListener("resize", checkTruncateDebounced);
      checkTruncateDebounced.cancel();
    };
  }, [checkTruncateDebounced]);

  const handleToggle = () => {
    if (!canToggle || isClosing) return;

    if (isExpanded) {
      setIsClosing(true);
      setIsExpanded(false);
      return;
    }

    setIsExpanded(true);
  };

  const handleEnter = useCallback(() => {
    setIsClosing(false);
  }, []);

  const handleExit = useCallback(() => {
    setIsClosing(true);
  }, []);

  const handleExited = useCallback(() => {
    setIsClosing(false);
    checkTruncate();
  }, [checkTruncate]);

  const shouldClamp = !isExpanded && !isClosing && canToggle;

  return (
    <Grid container>
      <Grid item xs={12}>
        <Collapse
          in={isExpanded}
          collapsedSize={collapsedSize}
          timeout={{ enter: 250, exit: 400 }}
          onEnter={handleEnter}
          onExit={handleExit}
          onExited={handleExited}
        >
          <MxTypography
            color={color}
            lines={shouldClamp ? lines : undefined}
            fontWeight={weight}
            variant="caption"
            ref={contentRef}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        </Collapse>
      </Grid>

      {canToggle && (
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "8px"
          }}
        >
          <ButtonContainer>
            <Button
              variant="text"
              onClick={handleToggle}
              style={{ textTransform: "none" }}
              startIcon={
                isExpanded ? (
                  <UpIcon width={18} height={18} />
                ) : (
                  <DownIcon width={18} height={18} />
                )
              }
            >
              <MxTypography
                color="primary.main"
                variant="overline"
                fontFamily={EFontFamilies.DISPLAY}
              >
                {isExpanded ? "Ver menos" : "Ver más"}
              </MxTypography>
            </Button>
          </ButtonContainer>
        </Grid>
      )}
    </Grid>
  );
};

export default memo(MXDescriptionToggle);

const ButtonContainer = styled("div")`
  width: 100%;
  display: flex;
  justify-content: center;
  svg {
    fill: var(--color-blue-main);
  }
`;
